import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { useUser } from '../components/UserContext';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useContracts } from "../contexts/DataContext";

import ConfiguracionOrganizacion from './ConfiguracionOrganizacion';
import ConfiguracionMarca from './ConfiguracionMarca';
import ConfiguracionComisiones from './ConfiguracionComisiones';
import ConfiguracionFacturacion from './ConfiguracionFacturacion';
import ConfiguracionUso from './ConfiguracionUso';
import ConfiguracionPerfil from './ConfiguracionPerfil';
import ConfiguracionCorreo from './ConfiguracionCorreo';

const TAB_LIST = [
  { key: "perfil", label: "Perfil" },
  { key: "organizacion", label: "Organización" },
  { key: "marca", label: "Marca" },
  { key: "correo", label: "Correo" },
  { key: "uso", label: "Uso" },
  { key: "comisiones", label: "Comisiones" },
  { key: "facturacion", label: "Facturación" },
];

const Configuracion = () => {
  const { user, userData, orgData, loading: userLoading, hasDependientes, isOwner, getUserEmail, getUserName, usersCache, fetchUserInfo } = useUser();
  const { contractsList } = useContracts();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get active tab from URL or default to "perfil"
  const getActiveTabFromURL = () => {
    const tabFromURL = searchParams.get('tab');
    const validTabs = TAB_LIST.map(tab => tab.key);
    return validTabs.includes(tabFromURL) ? tabFromURL : "perfil";
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromURL());

  // Function to handle tab changes and update URL
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', newTab);
      return newParams;
    });
  };

  // Sync activeTab state when URL changes (for browser back/forward)
  useEffect(() => {
    const tabFromURL = getActiveTabFromURL();
    if (tabFromURL !== activeTab) {
      setActiveTab(tabFromURL);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pagos (billing) state - fetched here for preloading
  const [pagos, setPagos] = useState([]);
  const [pagosLoading, setPagosLoading] = useState(false);
  const [pagosError, setPagosError] = useState(null);

  // Tarifas state - fetched here for preloading
  const [tarifas, setTarifas] = useState([]);
  const [tarifasLoading, setTarifasLoading] = useState(false);
  const [tarifasError, setTarifasError] = useState(null);

  // Plan state - fetched from orgs/{orgId}/plan/current
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  // Usage state - fetched from orgs/{orgId}/platform_usage/{YYYY_MM}
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // Storage usage state - fetched from orgs/{orgId}/storage_usage/current
  const [storageUsage, setStorageUsage] = useState(null);
  const [storageUsageLoading, setStorageUsageLoading] = useState(false);

  const chunkArray = (items, size = 10) => {
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  };

  // Fetch tarifas
  const fetchTarifas = useCallback(async () => {
    if (!isOwner) {
      setTarifas([]);
      return;
    }

    setTarifasLoading(true);
    setTarifasError(null);
    try {
      const results = [];

      if (userData?.org) {
        const orgQuery = query(
          collection(db, "tarifas"),
          where("org", "==", userData.org)
        );
        const snapshot = await getDocs(orgQuery);
        snapshot.forEach((d) => {
          results.push({ id: d.id, ...d.data() });
        });
      } else {
        if (!userData?.tarifas || userData.tarifas.length === 0) {
          setTarifas([]);
          setTarifasLoading(false);
          return;
        }
        const ids = userData.tarifas;
        const chunks = chunkArray(ids, 10);
        for (const chunk of chunks) {
          const q = query(collection(db, "tarifas"), where(documentId(), "in", chunk));
          const snapshot = await getDocs(q);
          snapshot.forEach((d) => {
            results.push({ id: d.id, ...d.data() });
          });
        }
      }

      setTarifas(results);
    } catch (error) {
      console.error("Error fetching tarifas:", error);
      setTarifasError("No se pudieron cargar las tarifas.");
    } finally {
      setTarifasLoading(false);
    }
  }, [userData?.tarifas, userData?.org, isOwner]);

  // Fetch pagos for org owners
  const fetchPagos = useCallback(async () => {
    if (!userData?.org || !isOwner) {
      setPagos([]);
      return;
    }

    setPagosLoading(true);
    setPagosError(null);
    try {
      const pagosQuery = query(
        collection(db, "pagos"),
        where("orgId", "==", userData.org),
        orderBy("date", "desc")
      );
      const snapshot = await getDocs(pagosQuery);
      const results = [];
      snapshot.forEach((d) => {
        results.push({ id: d.id, ...d.data() });
      });
      setPagos(results);
    } catch (error) {
      console.error("Error fetching pagos:", error);
      setPagosError("No se pudieron cargar los pagos.");
    } finally {
      setPagosLoading(false);
    }
  }, [userData?.org, isOwner]);

  // Fetch plan data from subcollection.
  // Plan reads are owner-only at the Firestore rule layer; skip the fetch for
  // non-owners to avoid a permission-denied error in the console.
  const fetchPlan = useCallback(async () => {
    if (!userData?.org || !isOwner) {
      setPlan(null);
      return;
    }

    setPlanLoading(true);
    try {
      const planDoc = await getDoc(doc(db, "orgs", userData.org, "plan", "current"));
      if (planDoc.exists()) {
        setPlan({ id: planDoc.id, ...planDoc.data() });
      } else {
        setPlan(null);
      }
    } catch (error) {
      console.error("Error fetching plan:", error);
    } finally {
      setPlanLoading(false);
    }
  }, [userData?.org, isOwner]);

  // Fetch usage data from subcollection
  const fetchUsage = useCallback(async () => {
    if (!userData?.org) {
      setUsage(null);
      return;
    }

    setUsageLoading(true);
    try {
      // Get current month in YYYY_MM format
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const usageDocId = `${year}_${month}`;
      const usageDoc = await getDoc(doc(db, "orgs", userData.org, "platform_usage", usageDocId));
      if (usageDoc.exists()) {
        setUsage({ id: usageDoc.id, ...usageDoc.data() });
      } else {
        setUsage({ total_pages: 0 });
      }
    } catch (error) {
      console.error("Error fetching usage:", error);
      setUsage({ total_pages: 0 });
    } finally {
      setUsageLoading(false);
    }
  }, [userData?.org]);

  // Fetch storage usage data from subcollection
  const fetchStorageUsage = useCallback(async () => {
    if (!userData?.org) {
      setStorageUsage(null);
      return;
    }

    setStorageUsageLoading(true);
    try {
      const storageDoc = await getDoc(doc(db, "orgs", userData.org, "storage_usage", "current"));
      if (storageDoc.exists()) {
        setStorageUsage({ id: storageDoc.id, ...storageDoc.data() });
      } else {
        setStorageUsage({ total_bytes: 0, file_count: 0 });
      }
    } catch (error) {
      console.error("Error fetching storage usage:", error);
      setStorageUsage({ total_bytes: 0, file_count: 0 });
    } finally {
      setStorageUsageLoading(false);
    }
  }, [userData?.org]);

  // Fetch data on mount
  useEffect(() => {
    if (!userLoading) {
      fetchTarifas();
      fetchPagos();
      fetchPlan();
      fetchUsage();
      fetchStorageUsage();
    }
  }, [userLoading, fetchTarifas, fetchPagos, fetchPlan, fetchUsage, fetchStorageUsage]);

  // Fetch org members' info for role display
  useEffect(() => {
    if (!orgData?.usuarios) return;

    orgData.usuarios.forEach(uid => {
      if (uid !== user?.uid && !usersCache[uid]) {
        fetchUserInfo(uid);
      }
    });
  }, [orgData?.usuarios, user?.uid, usersCache, fetchUserInfo]);

  // Filter tabs based on user permissions
  const visibleTabs = TAB_LIST.filter(tab => {
    if (tab.key === "marca") {
      // Gatekept: only owners of orgs the Surelia team has enabled.
      return userData?.org && isOwner && Boolean(orgData?.brandingEnabled);
    }
    if (tab.key === "comisiones" || tab.key === "facturacion" || tab.key === "uso") {
      return userData?.org && isOwner;
    }
    return true;
  });

  if (userLoading) {
    return (
      <div className="p-6">
        <Heading level={1}>Configuración</Heading>
        <Text className="mt-4">Cargando...</Text>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      {/* Tabs Navigation */}
      <div className="overflow-x-auto border-b border-azul-surelia/20">
        <div className="flex gap-1 min-w-max">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              className={`px-3 sm:px-6 py-3 font-semibold focus:outline-none transition-all duration-200 border-b-2 whitespace-nowrap text-sm sm:text-base ${
                activeTab === tab.key
                  ? "text-azul-surelia border-azul-surelia bg-azul-surelia/5"
                  : "text-gray-500 border-transparent hover:text-azul-surelia hover:border-azul-surelia/40 hover:bg-azul-surelia/5"
              }`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Panels */}

      {/* Perfil Tab */}
      {activeTab === "perfil" && (
        <ConfiguracionPerfil userData={userData} />
      )}

      {/* Correo Tab (todos los usuarios) */}
      {activeTab === "correo" && (
        <ConfiguracionCorreo />
      )}

      {/* Organización Tab */}
      {activeTab === "organizacion" && (
        <ConfiguracionOrganizacion
          user={user}
          userData={userData}
          orgData={orgData}
          isOwner={isOwner}
          hasDependientes={hasDependientes}
          usersCache={usersCache}
          fetchUserInfo={fetchUserInfo}
          getUserEmail={getUserEmail}
          getUserName={getUserName}
          plan={plan}
          planLoading={planLoading}
        />
      )}

      {/* Marca Tab */}
      {activeTab === "marca" && (
        <ConfiguracionMarca
          orgData={orgData}
          isOwner={isOwner}
          brandingEnabled={Boolean(orgData?.brandingEnabled)}
        />
      )}

      {/* Uso Tab */}
      {activeTab === "uso" && (
        <ConfiguracionUso
          plan={plan}
          planLoading={planLoading}
          usage={usage}
          usageLoading={usageLoading}
          storageUsage={storageUsage}
          storageUsageLoading={storageUsageLoading}
          orgData={orgData}
        />
      )}

      {/* Comisiones Tab */}
      {activeTab === "comisiones" && (
        <ConfiguracionComisiones
          user={user}
          userData={userData}
          tarifas={tarifas}
          setTarifas={setTarifas}
          tarifasLoading={tarifasLoading}
          tarifasError={tarifasError}
          setTarifasError={setTarifasError}
          contractsList={contractsList}
        />
      )}

      {/* Facturación Tab */}
      {activeTab === "facturacion" && (
        <ConfiguracionFacturacion
          pagos={pagos}
          pagosLoading={pagosLoading}
          pagosError={pagosError}
          orgData={orgData}
          plan={plan}
          planLoading={planLoading}
          usage={usage}
          usageLoading={usageLoading}
        />
      )}
    </div>
  );
};

export default Configuracion;
